import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import styles from '../PdfRenderer.module.scss';
import { services } from '../../../../../services';
import { getPDFNoteStatusBadge } from '../../../../../helpers/mapping/PDFNoteStatuses';
import type { PDFNote } from '../../../../../pages/notes/models/PDFNote';
import type { PDFNoteQuery } from '../../../../../pages/notes/models/PDFNoteQuery';
import { stripHtml } from '../helpers/formatting';
import type { ApplicationState } from '../../../../../store';

interface LinkNoteScreenProps {
    workflowInstanceId: number;
    noteObjectId: number;
    workflowInstanceAttachmentId: number;
    onSelect: (note: PDFNote) => void;
    onBack: () => void;
}

export function LinkNoteScreen({ workflowInstanceId, noteObjectId, workflowInstanceAttachmentId, onSelect, onBack }: LinkNoteScreenProps) {
    const userId = useSelector((state: ApplicationState) => state.context.userContext.Id);
    const [notes, setNotes] = useState<PDFNote[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const searchRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setLoading(true);
        services.pDFNoteService
            .fetchPaged({
                WorkflowInstanceIds: [workflowInstanceId],
                NoteObjectIds: [noteObjectId],
                AssigneeIds: [userId],
                Take: 100,
                Skip: 0,
            } as PDFNoteQuery)
            .then(res => {
                const all = res.data.Items ?? [];
                const filtered = workflowInstanceAttachmentId
                    ? all.filter(n =>
                        (n.NoteWorkflowInstanceAttachments ?? []).some(
                            a => Number(a.PDFWorkflowInstanceAttachmentId) === workflowInstanceAttachmentId
                        )
                    )
                    : all;
                setNotes(filtered);
            })
            .finally(() => setLoading(false));
    }, [workflowInstanceId, noteObjectId, workflowInstanceAttachmentId]);

    useEffect(() => {
        searchRef.current?.focus();
    }, []);

    const q = search.trim().toLowerCase();
    const filtered = q
        ? notes.filter(n => stripHtml(n.Title ?? '').toLowerCase().includes(q))
        : notes;

    return (
        <div className={styles.modalBackdrop} onClick={onBack}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <button type="button" className={styles.linkScreenBack} onClick={onBack}>
                        <i className="fa-solid fa-arrow-left" />
                    </button>
                    <span className={styles.modalTitle}>Выбрать существующее замечание</span>
                    <button type="button" className={styles.modalClose} onClick={onBack} aria-label="Закрыть">
                        <i className="fa-solid fa-xmark" />
                    </button>
                </div>

                <div className={styles.linkScreenSearch}>
                    <i className="fa-solid fa-magnifying-glass" />
                    <input
                        ref={searchRef}
                        type="text"
                        placeholder="Поиск по тексту замечания..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>

                <div className={styles.linkScreenList}>
                    {loading ? (
                        <div className={styles.linkScreenEmpty}>
                            <i className="pi pi-spin pi-spinner" /> Загрузка...
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className={styles.linkScreenEmpty}>Замечания не найдены</div>
                    ) : (
                        filtered.map(note => (
                            <button
                                key={note.Id}
                                type="button"
                                className={styles.linkScreenItem}
                                onClick={() => onSelect(note)}
                            >
                                <div className={styles.linkScreenItemHeader}>
                                    {note.StatusId != null && (
                                        <span className={getPDFNoteStatusBadge(note.StatusId)}>
                                            {note.Status?.Title ?? ''}
                                        </span>
                                    )}
                                    <span className={styles.linkScreenItemPage}>
                                        Стр. {note.PageInfo}
                                    </span>
                                </div>
                                <div className={styles.linkScreenItemTitle}>
                                    {stripHtml(note.Title ?? '') || '(без текста)'}
                                </div>
                            </button>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
